import React, {
  useEffect,
  useState,
  useRef,
} from 'react';
import styled from 'styled-components';
import { connect } from 'react-redux';
import { bindActionCreators } from 'redux';
import {
  Drawer,
  Layout,
  Modal,
} from 'antd';
import _ from 'lodash';

import i18n from '../../config/i18n';
import WebSocketActionCreators from '../../actions/WebSocketActionCreators';
import ChannelActionCreators from '../../actions/ChannelActionCreators';
import * as ActionCreators from '../../actions/ActionCreators';
import ChannelAPIConnectors from '../../api/ChannelAPIConnectors';
import ChannelDetailView from './ChannelDetailView';
import ChannelList from '../../components/messaging/ChannelList';
import '../../styles/css/messaging.css';
import CreateChannelForm from '../../components/messaging/CreateChannelForm';
import {
  ADMIN_ID,
} from '../../constants/RoleConstants';

const { Sider } = Layout;

const BlankContentView = styled.div`
  width: 100%;
  height: 100%;
  background-color: #ecf0f1;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  text-align: center;
`;

const styles = {
  root: {
    position: 'relative',
    height: '90vh',
  },
  channelList: {
    width: '100%',
    height: '100%',
  },
  drawer: {
    base: { position: 'absolute', touchAction: 'none' },
    body: { padding: 0 },
  },
};


const MessagingContainer = ({
  actions: {
    ackChannel,
    updateChannel,
    createAlert,
    createChannel,
    createInfoAlert,
    createSuccessAlert,
    fetchAllChannelDetailInformation,
    fetchChannels,
    fetchTaggableMedicalRecords,
    setCurrentChannel,
    websocketConnect,
    websocketDisconnect,
    getInvitableUsers,
  },
  channels,
  currentChannelId,
  currentUserId,
  currentUserRoleId,
  invitableUsers,
  router,
}) => {
  const hasActiveChannel = currentChannelId > 0;
  const [isChannelListCollapsed, setIsChannelListCollapsed] = useState(false);
  const [isWindowSmall, setIsWindowSmall] = useState(false);
  const [isModalVisible, setIsModalVisible] = useState(false);
  const isNewChannelCreatedRef = useRef(false);
  const newMembersIds = useRef([]);

  useEffect(() => {
    if (hasActiveChannel) fetchAllChannelDetailInformation(currentChannelId);
    getInvitableUsers(currentChannelId);

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentChannelId]);

  useEffect(() => {
    websocketConnect();
    fetchChannels();
    return websocketDisconnect;

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleOpenChannelList = () => {
    setIsChannelListCollapsed(false);
  };

  const handleCloseChannelList = (e) => {
    e.preventDefault();
    setIsChannelListCollapsed(true);
  };

  const handleCheckoutChannel = async (channelId) => {
    if (isWindowSmall && !isChannelListCollapsed) {
      setIsChannelListCollapsed(true);
    }
    if (currentChannelId === channelId) { return; }

    ackChannel(channelId);
    updateChannel({ channelId, unReadCnt: 0 });
    setCurrentChannel(channelId);
  };

  const handleAddChannelMembers = async (channelId, userIds) => {
    try {
      await ChannelAPIConnectors.addChannelMember(channelId, userIds);
    } catch (err) {
      createAlert(err);
    }
    await getInvitableUsers(channelId);
    if (currentUserRoleId !== ADMIN_ID) await fetchTaggableMedicalRecords(channelId);
  };

  const handleStartCreateChannel = async () => {
    setIsModalVisible(true);
    await getInvitableUsers(0);
  };
  const handleCancelCreateChannel = () => {
    setIsModalVisible(false);
  };
  const handleConfirmCreateChannel = (channelData) => {
    createInfoAlert(i18n.t('startCreateChannelMsg'));
    setIsModalVisible(false);
    isNewChannelCreatedRef.current = true;
    newMembersIds.current = channelData.members;
    createChannel({ displayName: channelData.name });
  };
  const afterCreateChannel = async () => {
    const newChannelId = channels.reduce(
      (largestId, channel) => (
        (channel.id > largestId && channel.creatorId === currentUserId) ? channel.id : largestId
      ), 0
    );
    await handleAddChannelMembers(newChannelId, newMembersIds.current);
    await handleCheckoutChannel(newChannelId);
    newMembersIds.current = [];
    createSuccessAlert(i18n.t('createChannelSuccessMsg'));
  };

  // If new channel is created then add members.
  useEffect(() => {
    if (isNewChannelCreatedRef.current) {
      afterCreateChannel();
      isNewChannelCreatedRef.current = false;
    }

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [channels]);

  return (
    <Layout
      style={styles.root}
    >
      {/** Channel List */}
      {
        isWindowSmall && (
          <Drawer
            bodyStyle={styles.drawer.body}
            closable={hasActiveChannel}
            getContainer={false}
            onClose={handleCloseChannelList}
            placement="left"
            style={styles.drawer.base}
            visible={!isChannelListCollapsed || !hasActiveChannel}
          >
            <ChannelList
              channels={channels}
              currentChannelId={currentChannelId}
              currentUserRoleId={currentUserRoleId}
              onCheckOutChannel={handleCheckoutChannel}
              onCreateChannel={handleStartCreateChannel}
            />
          </Drawer>
        )
      }
      <Sider
        breakpoint="md"
        collapsedWidth={0}
        onBreakpoint={setIsWindowSmall}
        trigger={null}
        width={300}
        theme="light"
      >
        {
          !isWindowSmall && (
            <ChannelList
              channels={channels}
              currentChannelId={currentChannelId}
              currentUserRoleId={currentUserRoleId}
              onCheckOutChannel={handleCheckoutChannel}
              onCreateChannel={handleStartCreateChannel}
            />
          )
        }
      </Sider>

      {/** Channel Detail */}
      {
        hasActiveChannel ? (
          <ChannelDetailView
            canOpenChannelList={isWindowSmall}
            invitableUsers={invitableUsers}
            onAddMembers={userIds => handleAddChannelMembers(currentChannelId, userIds)}
            onOpenChannelList={handleOpenChannelList}
            router={router}
          />
        ) : (
          <BlankContentView>
            <h2>{i18n.t('clickChatRoomMsg')}</h2>
            <h3>{i18n.t('startConversationMsg')}</h3>
          </BlankContentView>
        )
      }

      {/** Create Channel */}
      <Modal
        bodyStyle={{ padding: 0 }}
        closable={false}
        visible={isModalVisible}
        onCancel={handleCancelCreateChannel}
        footer={null}
      >
        <CreateChannelForm
          invitableUsers={invitableUsers}
          onCancel={handleCancelCreateChannel}
          onSubmit={handleConfirmCreateChannel}
        />
      </Modal>
    </Layout>
  );
};

const mapStateToProps = (state) => {
  const {
    channel: {
      channels,
      currentChannelId,
      invitableUsers,
    },
    user: {
      language,
      profile: { roleId },
      userId,
    },
  } = state;

  return {
    channels: channels.sort((first, second) => (_.get(second, ['lastMsg', 'id'], 0) - _.get(first, ['lastMsg', 'id'], 0))),
    currentChannelId,
    currentUserId: parseInt(userId, 10),
    currentUserRoleId: roleId,
    invitableUsers,
    language, // to trigger component re-render
  };
};

const mapDispatchToProps = (dispatch) => {
  const allActions = {
    ...ActionCreators,
    ...ChannelActionCreators,
    ...WebSocketActionCreators,
  };

  return {
    actions: bindActionCreators(allActions, dispatch),
  };
};

export default connect(
  mapStateToProps,
  mapDispatchToProps
)(MessagingContainer);



// WEBPACK FOOTER //
// ./src/containers/messaging/MessagingContainer.js